import { useForm, useWatch } from "react-hook-form";
import { useAppSelector } from "store/store";

export type AllocationFormValues = {
  allocType: string;
  allocationBy: string;
  useCurrentAllocation: any;
  rebalanceUsing: string;
  weightageDistributionMethod: string;
  portfolios: any[];
  companies: any[];
  allocVersion: any;
  exitWeightage: string | number;
  stockWise: boolean;
  frozenCompanies: any[];
  holdCompanies: any[];
  buyCompanies: any[];
  sellCompanies: any[];
  exitParameter: string;
  buyParameter: string;
};

export type OrderFormValues = {
  basket: any;
  transactionType: string;
  allocation: AllocationFormValues;
  extraThings: {
    rightVisibleTable: any;
  };
  portfolioSelectionFilter: {
    portfolio_group: any[];
    portfolio_value: any[];
    available_cash: any[];
  };
  generateTradeResponse: any[];
  customQuantities: any[];
  generateOrderSheet: {
    rationale: string;
    limitOrder: boolean;
  };
  generateResponsePayload: any;
};

export const getInitialAllocationValues = (): AllocationFormValues => ({
  allocType: "",
  allocationBy: "",
  useCurrentAllocation: null,
  rebalanceUsing: "",
  weightageDistributionMethod: "",
  portfolios: [],
  companies: [],
  allocVersion: null,
  exitWeightage: "",
  stockWise: false,
  frozenCompanies: [],
  holdCompanies: [],
  buyCompanies: [],
  sellCompanies: [],
  exitParameter: "",
  buyParameter: "",
});

export const getInitialOrderFormValues = (): OrderFormValues => ({
  basket: null,
  transactionType: "",
  allocation: getInitialAllocationValues(),
  extraThings: {
    rightVisibleTable: null,
  },
  portfolioSelectionFilter: {
    portfolio_group: [],
    portfolio_value: [],
    available_cash: [],
  },
  generateTradeResponse: [],
  customQuantities: [],
  generateOrderSheet: {
    rationale: "",
    limitOrder: false,
  },
  generateResponsePayload: {},
});

const useOrders = () => {
  const userData = useAppSelector((state) => state?.user?.userData);
  const companies = useAppSelector((state) => state?.app?.companies);

  // const {
  //   data: allocationVersionList,
  //   isFetching: allocationVersionListLoading,
  // } = useFetchQuery({
  //   key: ["MODEL_PORTFOLIO_ALLOCATION_VERSION_LIST"],
  //   route: `/model_portfolio/${modelData?.id}/allocation-versions/`,
  //   enabled: !!modelData?.id,
  // });

  const form = useForm<OrderFormValues>({
    defaultValues: getInitialOrderFormValues(),
  });

  const watchAllocationType = useWatch({
    control: form.control,
    name: "allocation.allocType",
  });

  const watchAllocationBy = useWatch({
    control: form.control,
    name: "allocation.allocationBy",
  });
  const watchRightVisibleTable = useWatch({
    control: form.control,
    name: "extraThings.rightVisibleTable",
  });
  const watchPortfolioFilter = useWatch({
    control: form.control,
    name: "portfolioSelectionFilter",
  });

  const ALLOCATION_TYPE = [
    { name: "Allocation", value: "allocation" },
    { name: "Rebalance", value: "rebalance" },
    { name: "Exit", value: "exit" },
  ];

  const ALLOCATION_BY = [
    { name: "Model", value: "version" },
    { name: "Company", value: "company" },
    { name: "Customized", value: "customized" },
  ];

  const TRANSACTION_TYPE = [
    { name: "Allocated", value: "allocated" },
    { name: "Current", value: "current" },
  ];

  const REFERENCE_TYPE_FOR_HEADER_CARD = [
    { name: "Current Value", value: "current" },
    { name: "Portfolio Value", value: "portfolio" },
    { name: "Portfolio Value - Absolute", value: "portfolioAbsolute" },
    { name: "Current Value - Absolute", value: "currentAbsolute" },
    { name: "Committed Value", value: "committedValue" },
    { name: "Committed Value - Absolute", value: "commitedValueAbsolute" },
    {
      name: userData?.useCeAmount ? "Cash & Cash Equivalent" : "Ledger Balance",
      value: "ledgerBalance",
    },
  ];

  const REFERENCE_TYPE = [
    {
      name: "Current Value",
      value: "current",
      allocationType: ["rebalance", "allocation", "exit"],
      rebalance: ["company", "version", "customized"],
      allocation: ["company"],
      exit: ["companyWeightage"],
    },
    {
      name: "Portfolio Value",
      value: "portfolio",
      allocationType: ["rebalance", "allocation", "exit"],
      rebalance: ["company", "version", "customized"],
      allocation: ["company"],
      exit: ["companyWeightage"],
    },
    {
      name: "Portfolio Value - Absolute",
      value: "portfolioAbsolute",
      allocationType: ["allocation"],
      rebalance: [] as string[],
      allocation: ["company"],
    },
    {
      name: "Current Value - Absolute",
      value: "currentAbsolute ",
      allocationType: ["allocation"],
      rebalance: [],
      allocation: ["company"],
    },
    {
      name: "Committed Value",
      value: "committedValue",
      allocationType: ["rebalance", "allocation", "exit"],
      rebalance: ["company", "version"],
      allocation: ["company", "version"],
      exit: ["companyWeightage"],
    },
    {
      name: "Committed Value - Absolute",
      value: "commitedValueAbsolute",
      allocationType: ["allocation"],
      rebalance: [],
      allocation: ["version", "company"],
    },
    {
      name: userData?.useCeAmount ? "Cash & Cash Equivalent" : "Ledger Balance",
      value: "ledgerBalance",
      allocationType: ["allocation"],
      rebalance: [],
      allocation: ["company", "version"],
    },
  ];

  const WEIGHTAGE_DISTRIBUTION_METHOD = [
    { value: "PRORATA", name: "Prorata" },
    { value: "FIXED", name: "No Redistribution" },
  ];

  const ALLOCATION_BY_EXIT = [
    { name: "Model", disabled: false, value: "version" },
    { name: "Portfolio", disabled: false, value: "portfolio" },
    { name: "Company-Absolute", disabled: false, value: "company" },
    { name: "Company-Weightage", disabled: false, value: "companyWeightage" },
  ];

  const CUSTOMIZED_PARAMETER = [
    { value: "companyAbsolute", name: "Company-Absolute" },
    { value: "companyFinal", name: "Company-Final Weightage" },
    { value: "weightageWise", name: "Weightage of CV/PV" },
    { value: "sellAmountAllocation", name: "Sell Amount-Allocation" },
  ];

  return {
    form,
    ALLOCATION_TYPE,
    ALLOCATION_BY,
    TRANSACTION_TYPE,
    watchAllocationType,
    watchAllocationBy,
    watchRightVisibleTable,
    watchPortfolioFilter,
    REFERENCE_TYPE_FOR_HEADER_CARD,
    REFERENCE_TYPE,
    WEIGHTAGE_DISTRIBUTION_METHOD,
    ALLOCATION_BY_EXIT,
    CUSTOMIZED_PARAMETER,
    companies,
  };
};

export default useOrders;
